import { fabric } from "fabric";

/**
 * "Placement mode": crosshair guides and a draft rectangle shown while the user
 * is dropping a new annotation onto the canvas. Mixed into BboxCanvas.prototype.
 */
export default {
    setPlacementMode(enabled, placementInfo = null) {
        this.placementMode = !!enabled;
        this.placementInfo = placementInfo || null;
        if (!this.placementMode) {
            this.clearPlacementPreview();
        }
        if (this.canvas) {
            this.canvas.defaultCursor = this.placementMode ? 'crosshair' : 'default';
            this.canvas.hoverCursor = this.placementMode ? 'crosshair' : 'move';
        }
        this._setAnnotationsPlacementPassthrough(this.placementMode);
    },

    _setAnnotationsPlacementPassthrough(passThrough) {
        if (!this.canvas) {
            return;
        }
        const interactive = !passThrough && !this.read_only;
        if (passThrough) {
            this.canvas.discardActiveObject();
        }
        this.canvas.getObjects().forEach((obj) => {
            if (!obj.annotation_id || obj.text_box || obj.placementGuide) {
                return;
            }
            obj.set({
                evented: interactive,
                selectable: interactive,
            });
        });
        this._updateBackgroundImageEvented();
        this.requestRenderAll();
    },

    _placementGuideColors() {
        const rgb = this.placementInfo?.color_values || [255, 255, 255];
        return {
            stroke: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.95)`,
            fill: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.14)`,
        };
    },

    _placementGuideDefaults() {
        return {
            selectable: false,
            evented: false,
            hasControls: false,
            hasBorders: false,
            lockMovementX: true,
            lockMovementY: true,
            placementGuide: true,
            excludeFromExport: true,
        };
    },

    clearPlacementPreview() {
        this.placementAnchor = null;
        [this.placementHLine, this.placementVLine, this.placementDraftRect].forEach((obj) => {
            if (obj) {
                this.canvas.remove(obj);
            }
        });
        this.placementHLine = null;
        this.placementVLine = null;
        this.placementDraftRect = null;
        this.requestRenderAll();
    },

    _bringPlacementGuidesToFront() {
        [this.placementHLine, this.placementVLine, this.placementDraftRect].forEach((obj) => {
            if (obj) {
                this.canvas.bringToFront(obj);
            }
        });
    },

    updatePlacementPreview(normX, normY) {
        if (!this.canvas || !this.placementMode) {
            return;
        }

        const cw = this.canvasWidth || this.canvas.getWidth() || 1;
        const ch = this.canvasHeight || this.canvas.getHeight() || 1;
        const px = normX * cw;
        const py = normY * ch;
        const { stroke, fill } = this._placementGuideColors();
        const guideDefaults = this._placementGuideDefaults();

        if (!this.placementHLine) {
            this.placementHLine = new fabric.Line([0, py, cw, py], {
                ...guideDefaults,
                stroke,
                strokeWidth: 1,
                strokeDashArray: [6, 4],
            });
            this.canvas.add(this.placementHLine);
        } else {
            this.placementHLine.set({ x1: 0, y1: py, x2: cw, y2: py });
        }

        if (!this.placementVLine) {
            this.placementVLine = new fabric.Line([px, 0, px, ch], {
                ...guideDefaults,
                stroke,
                strokeWidth: 1,
                strokeDashArray: [6, 4],
            });
            this.canvas.add(this.placementVLine);
        } else {
            this.placementVLine.set({ x1: px, y1: 0, x2: px, y2: ch });
        }

        if (this.placementInfo?.type === 'box' && this.placementAnchor) {
            const ax = this.placementAnchor.x * cw;
            const ay = this.placementAnchor.y * ch;
            const left = Math.min(ax, px);
            const top = Math.min(ay, py);
            const width = Math.abs(px - ax);
            const height = Math.abs(py - ay);

            if (!this.placementDraftRect) {
                this.placementDraftRect = new fabric.Rect({
                    ...guideDefaults,
                    left,
                    top,
                    width: Math.max(width, 1),
                    height: Math.max(height, 1),
                    fill,
                    stroke,
                    strokeWidth: 2,
                    strokeDashArray: [8, 4],
                    strokeUniform: true,
                });
                this.canvas.add(this.placementDraftRect);
            } else {
                this.placementDraftRect.set({
                    left,
                    top,
                    width: Math.max(width, 1),
                    height: Math.max(height, 1),
                    fill,
                    stroke,
                });
            }
        } else if (this.placementDraftRect) {
            this.canvas.remove(this.placementDraftRect);
            this.placementDraftRect = null;
        }

        this._bringPlacementGuidesToFront();
        this.requestRenderAll();
    },
};
